/**
 * WHERE A QUOTE ACTUALLY IS.
 *
 * Anthony: "Allow for sort between quote status. statuses include not sent,
 * sent, approved, modifications requested, cancelled."
 *
 * Every quote in the shop sat under one word, "Quote", so a price nobody
 * has sent, a price sent three days ago and heard nothing about, and a
 * price a customer has already said yes to were the same row on the same
 * screen. The five states are what an advisor is actually sorting by when
 * they open that page.
 *
 * FOUR OF THE FIVE ARE ALREADY KNOWABLE and were simply never asked:
 *
 *   not sent   an estimate with no expiry, because the expiry is stamped
 *              at the moment it goes out and nowhere else
 *   sent       an estimate with one
 *   approved   an estimate with an authorization recorded against it
 *   cancelled  the job's own status
 *
 * Deriving beats storing here. A stored `quote_state` is a column that can
 * disagree with the authorization sitting next to it, and when those two
 * disagree the shop believes the wrong one.
 *
 * THE FIFTH HAD TO BE RECORDED, because nothing in the system knew it. A
 * customer who reads a quote and says "can you do it without the rear
 * pads" is neither waiting nor approved, and looked exactly like somebody
 * who had not opened the text. It is the pile that most needs to be
 * visible, because it is the one where the shop owes an answer.
 */

export type QuoteState =
  | "not_sent" | "sent" | "changes" | "approved" | "cancelled";

export const QUOTE_STATES: Array<{ key: QuoteState; label: string; blurb: string }> = [
  { key: "not_sent", label: "Not sent", blurb: "Priced and still sitting here." },
  { key: "sent", label: "Sent", blurb: "Gone to them, waiting on a yes." },
  { key: "changes", label: "Changes asked for", blurb: "They came back. We owe them an answer." },
  { key: "approved", label: "Approved", blurb: "They said yes. Book it." },
  { key: "cancelled", label: "Cancelled", blurb: "Closed without the work." },
];

export const quoteStateLabel = (s: QuoteState): string =>
  QUOTE_STATES.find((x) => x.key === s)?.label ?? s;

export type QuoteFactsForState = {
  status: string;
  /** Stamped when the quote is first sent, and only then. */
  quoteExpiresAt?: string | null;
  /** Set when the customer asks for something different. */
  changesRequestedAt?: string | null;
  /** Any authorization recorded against this job. */
  hasAuthorization?: boolean;
};

/**
 * The order matters and it is the order a person would read it in.
 *
 * CANCELLED FIRST, because a cancelled quote is cancelled whatever else is
 * true of it. APPROVED NEXT, because a yes outranks a later question: if
 * they authorized it and then asked about something, the work is on and
 * the question is a conversation, not a hold. Then changes, then sent,
 * then the one nobody has done anything with.
 */
export function quoteStateOf(j: QuoteFactsForState): QuoteState {
  if (j.status === "cancelled") return "cancelled";
  if (j.hasAuthorization) return "approved";
  if (j.changesRequestedAt) return "changes";
  if (j.quoteExpiresAt) return "sent";
  return "not_sent";
}
